import moment from "moment";
import React, { useEffect } from "react";
import { BooleanField, Datagrid, FunctionField, ListContextProvider, TextField, useListContext } from "react-admin";
import { useHistory } from "react-router-dom";
import { RDDDatetimeField } from "../../../../Components/RDDDatetimeField";
import { CustomDateField } from "../../../../Components/CustomDateField";
import CustomLinearProgress from "../../../../Components/Progress/LinearProgress";
import CreatedByField from "../../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { RequestListGroupType } from "../../../../Enums/RequestListGroupType";
import ListCustomButtons from "./RequestListActions";
import WarehouseInfo from "./WarehouseInfo";
import AcRegField from "../../../../../src/Components/ReferenceFields/AcReg/AcRegField";
import ColoredTextFieldForReqList from "../../../../../src/Components/ColoredTextFieldForReqList/ColoredTextFieldForReqList";
import CopyableTableWrapper from "../../../../Components/CopyableTableWrapper";
import { formatDate } from "../../../../Helper/MomentHelper";

export const RequestOrderDataGrid = ({
  record,
  requestStatus,
  isXSmall,
  setActiveTab,
  remark,
  createId,
  setRemark,
  handleOpenWorkFlow,
  ...rest
}) => {
  const listContext = useListContext();
  const history = useHistory();
  const { ids, filterValues, setFilters, displayedFilters, loading, data } = listContext;
  useEffect(() => {
    setFilters({ ...filterValues, requestStatus__eq: requestStatus }, displayedFilters);
  }, [requestStatus]);
  useEffect(() => {
    if ((history as any)?.location?.state?.requestStatus__eq) {
      setFilters({ ...filterValues, ...(history as any)?.location?.state }, displayedFilters);
      setActiveTab((history as any)?.location?.state?.requestStatus__eq);
      history.replace({
        pathname: `/requestorders/list`,
        state: {},
      });
    }
  }, []);

  const onEdit = (id, basePat, record) => {
    if (basePat == "/requestremarks/requestorders/") {
      rest?.onCreateRemarks?.(record);
    } else {
      const win: any = window.open(`#/requestorders/${id}`, "_blank");
      win.focus();
    }
  };

  const WarehousePanel = props => <WarehouseInfo pn={props.record.customerRequestComponent} />;

  const getDataGrid = () => {
    switch (requestStatus) {
      case RequestListGroupType.OpenNotSet:
        return (
          <CopyableTableWrapper>
            <Datagrid optimized>
              <ListCustomButtons onEdit={onEdit} handleOpenWorkFlow={handleOpenWorkFlow} />
              <TextField source="refNo" label="Ref No" />
              <TextField source="ownerCode" label="Customer" />
              <BooleanField source="access" label="Pool Scope" />
              <TextField source="airportCode" label="Destination" />
              <TextField source="customerRequestComponent" label="Pn" />
              <TextField source="componentDescription" label="Description" />
              <TextField source="repairTat" label="Repair TAT" />
              <RDDDatetimeField label="Request Delivery Date" />
              <ColoredTextFieldForReqList source="whCount" displaySource="displayWhCount" label="Wh" sortable={false} />
              <ColoredTextFieldForReqList
                source="resCount"
                displaySource="displayResCount"
                label="Res"
                sortable={false}
              />
              <ColoredTextFieldForReqList
                source="altPnCount"
                displaySource="displayAltPnCount"
                label="Alt. Pn"
                sortable={false}
              />
              <ColoredTextFieldForReqList source="woCount" displaySource="displayWoCount" label="Wo" sortable={false} />
              <ColoredTextFieldForReqList source="roCount" displaySource="displayRoCount" label="Ro" sortable={false} />
              <ColoredTextFieldForReqList
                source="poExCount"
                displaySource="displayPoExCount"
                label="Po/Ex"
                sortable={false}
              />
              <AcRegField label="AC-REG" link={false} />
              <TextField
                style={{
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  lineBreak: "anywhere",
                  minWidth: 200,
                }}
                aria-multiline={"true"}
                component={"pre"}
                source="currentPbhNote"
                label="Current Pbh Note"
                sortable={false}
              />
            </Datagrid>
          </CopyableTableWrapper>
        );
      case RequestListGroupType.InProgress:
        return (
          <CopyableTableWrapper>
            <Datagrid optimized expand={<WarehousePanel />} {...rest} hasBulkActions={false}>
              <ListCustomButtons
                label={"Actions"}
                isShowSendReplacementWaitingButton={true}
                isShowReserveForm={true}
                handleOpenWorkFlow={handleOpenWorkFlow}
                onEdit={onEdit}
              />
              <TextField source="refNo" label="Ref No" />
              {/* <RequestStatusField label="Status" /> */}
              <TextField
                source="requestStatusText"
                label="Status"
                style={{ color: "#cc0000" }}
                sortBy="requestStatus"
              />
              <TextField source="customerCode" label="Customer" />
              <TextField source="airportCode" label="Destination" />
              <TextField source="customerRequestComponent" label="Pn" />
              <TextField source="componentDescription" label="Description" />
              <TextField source="repairTat" label="Repair TAT" />
              <TextField source="workshop" label="Workshop" />
              <RDDDatetimeField label="Request Delivery Date" />
              <FunctionField
                sortBy="requestedDeliveryDate"
                label="Days Remaining"
                render={(record: any) => {
                  return record.requestedDeliveryDate === null
                    ? "Not Arrived Yet"
                    : formatDate(record?.requestedDeliveryDate, false) == formatDate(new Date("01/01/9999"), false)
                      ? "Exclude"
                      : moment.utc(record?.requestedDeliveryDate).diff(moment.utc(), "days");
                }}
              />
              <ColoredTextFieldForReqList source="whCount" displaySource="displayWhCount" label="Wh" sortable={false} />
              <ColoredTextFieldForReqList
                source="resCount"
                displaySource="displayResCount"
                label="Res"
                sortable={false}
              />
              <ColoredTextFieldForReqList
                source="altPnCount"
                displaySource="displayAltPnCount"
                label="Alt. Pn"
                sortable={false}
              />
              <ColoredTextFieldForReqList source="woCount" displaySource="displayWoCount" label="Wo" sortable={false} />
              <ColoredTextFieldForReqList source="roCount" displaySource="displayRoCount" label="Ro" sortable={false} />
              <ColoredTextFieldForReqList
                source="poExCount"
                displaySource="displayPoExCount"
                label="Po/Ex"
                sortable={false}
              />
              <ColoredTextFieldForReqList source="rnCount" label="Rn" sortable={false} />
              <AcRegField label="AC-REG" link={false} />
              <TextField source="customerPo" label="Customer PO" />
              <TextField source="ttWo" label="TT-WO" sortable={false} />
              <TextField source="ttRo" label="TT-RO" sortable={false} />
              <TextField
                style={{
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  lineBreak: "anywhere",
                  minWidth: 200,
                }}
                aria-multiline={"true"}
                component={"pre"}
                source="currentPbhNote"
                label="Current Pbh Note"
                sortable={false}
              />
            </Datagrid>
          </CopyableTableWrapper>
        );
      case RequestListGroupType.RepairedUnitShipped:
        return (
          <CopyableTableWrapper>
            <Datagrid optimized>
              <ListCustomButtons onEdit={onEdit} handleOpenWorkFlow={handleOpenWorkFlow} />
              <TextField source="refNo" label="Ref No" />
              <TextField
                source="requestStatusText"
                label="Status"
                style={{ color: "#cc0000" }}
                sortBy="requestStatus"
              />
              <TextField source="customerCode" label="Customer" />
              <TextField source="airportCode" label="Destination" />
              <TextField source="customerRequestComponent" label="Pn" />
              <TextField source="componentDescription" label="Description" />
              <TextField source="repairTat" label="Repair TAT" />
              <RDDDatetimeField label="Request Delivery Date" />
              <AcRegField label="AC-REG" link={false} />
              <TextField source="customerPo" label="Customer PO" />
              <TextField source="ttWo" label="TT-WO" sortable={false} />
              <TextField source="ttRo" label="TT-RO" sortable={false} />
              <TextField
                style={{
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  lineBreak: "anywhere",
                  minWidth: 200,
                }}
                aria-multiline={"true"}
                component={"pre"}
                source="currentPbhNote"
                label="Current Pbh Note"
                sortable={false}
              />
            </Datagrid>
          </CopyableTableWrapper>
        );
      case RequestListGroupType.Rejected:
        return (
          <CopyableTableWrapper>
            <Datagrid optimized>
              <TextField source="refNo" label="Ref No" />
              <TextField source="componentDescription" label="Description" />
              <FunctionField
                label="Current Pbh Note"
                render={record =>
                  record?.currentPbhNote?.length > 20
                    ? record?.currentPbhNote?.slice?.(0, 20) + "..."
                    : record?.currentPbhNote || null
                }
                tooltip="currentPbhNote"
              />
              <CreatedByField label="Created By" link={false} />
              <CustomDateField source="createdDate" label="Created Date" showTime />
            </Datagrid>
          </CopyableTableWrapper>
        );
      default:
        return (
          <CopyableTableWrapper>
            <Datagrid optimized>
              <ListCustomButtons onEdit={onEdit} detailButtonDisabled={true} handleOpenWorkFlow={handleOpenWorkFlow} />
              <TextField source="refNo" label="Ref No" />
              <TextField source="componentDescription" label="Description" />
              <TextField
                style={{
                  width: "100%",
                  whiteSpace: "pre-wrap",
                  lineBreak: "anywhere",
                  minWidth: 200,
                }}
                aria-multiline={"true"}
                component={"pre"}
                source="currentPbhNote"
                label="Current Pbh Note"
              />
              <CreatedByField label="Created By" link={false} />
              <CustomDateField source="createdDate" label="Created Date" showTime />
            </Datagrid>
          </CopyableTableWrapper>
        );
    }
  };

  return (
    <>
      {isXSmall ? (
        <ListContextProvider {...rest} value={{ ...listContext, ids: ids }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          {...rest}
          value={{
            ...listContext,
            ids: ids,
          }}
        >
          <CustomLinearProgress loading={loading} periodTime={500} />
          {!loading && getDataGrid()}
        </ListContextProvider>
      )}
    </>
  );
};

export default RequestOrderDataGrid;
